import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Anchor, Ship, Waves, Eye, EyeOff, BookOpen, Brain, BarChart3, Trophy } from 'lucide-react';
import ThemeToggle from '@/components/common/ThemeToggle';
import PasswordStrengthIndicator from './PasswordStrengthIndicator';
import { useToast } from '@/hooks/use-toast';
import { lovable } from '@/integrations/lovable';
import { supabase } from '@/integrations/supabase/client';
import { z } from 'zod';

const emailSchema = z.string().email('Please enter a valid email address');

interface AuthScreenProps {
  onSignUp: (email: string, password: string, name?: string) => Promise<{ user: any; error: Error | null }>;
  onSignIn: (email: string, password: string) => Promise<{ user: any; error: Error | null }>;
}

const AuthScreen = ({ onSignUp, onSignIn }: AuthScreenProps) => {
  const [isLogin, setIsLogin] = useState(true);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isAppleLoading, setIsAppleLoading] = useState(false);
  const [isResetLoading, setIsResetLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [termsError, setTermsError] = useState('');
  const [emailError, setEmailError] = useState('');
  const { toast } = useToast();

  const validateEmail = (value: string): boolean => {
    const result = emailSchema.safeParse(value);
    if (!result.success) {
      setEmailError(result.error.errors[0].message);
      return false;
    }
    setEmailError('');
    return true;
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    if (emailError && value) {
      validateEmail(value);
    }
  };

  const handleEmailBlur = () => {
    if (email) {
      validateEmail(email);
    }
  };

  const handleGoogleSignIn = async () => {
    setIsGoogleLoading(true);
    try {
      const { error } = await lovable.auth.signInWithOAuth('google', {
        redirect_uri: window.location.origin,
      });
      if (error) {
        toast({
          title: 'Google Sign In Failed',
          description: error.message || 'Could not sign in with Google.',
          variant: 'destructive',
        });
      }
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Something went wrong. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleAppleSignIn = async () => {
    setIsAppleLoading(true);
    try {
      const { error } = await lovable.auth.signInWithOAuth('apple', {
        redirect_uri: window.location.origin,
      });
      if (error) {
        toast({
          title: 'Apple Sign In Failed',
          description: error.message || 'Could not sign in with Apple.',
          variant: 'destructive',
        });
      }
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Something went wrong. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsAppleLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      toast({
        title: 'Email Required',
        description: 'Please enter your email address.',
        variant: 'destructive',
      });
      return;
    }

    setIsResetLoading(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/`,
      });

      if (error) {
        toast({
          title: 'Reset Failed',
          description: error.message || 'Could not send reset email.',
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Check Your Email',
          description: 'We sent you a password reset link. Please check your inbox.',
        });
        setIsForgotPassword(false);
      }
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Something went wrong. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsResetLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast({
        title: 'Missing Fields',
        description: 'Please enter both email and password.',
        variant: 'destructive',
      });
      return;
    }

    if (!validateEmail(email)) {
      return;
    }

    if (password.length < 6) {
      toast({
        title: 'Password Too Short',
        description: 'Password must be at least 6 characters.',
        variant: 'destructive',
      });
      return;
    }

    if (!isLogin && password !== confirmPassword) {
      setConfirmPasswordError('Passwords do not match');
      return;
    }

    if (!isLogin && !agreedToTerms) {
      setTermsError('You must agree to the Terms of Service and Privacy Policy');
      return;
    }

    setIsLoading(true);

    try {
      if (isLogin) {
        const { error } = await onSignIn(email, password);
        if (error) {
          toast({
            title: 'Sign In Failed',
            description: error.message || 'Invalid email or password.',
            variant: 'destructive',
          });
        }
      } else {
        const { user, error } = await onSignUp(email, password, name || undefined);
        if (error) {
          toast({
            title: 'Sign Up Failed',
            description: error.message || 'Could not create account.',
            variant: 'destructive',
          });
        } else if (user) {
          toast({
            title: 'Check Your Email',
            description: 'We sent you a confirmation link. Please verify your email to continue.',
          });
        }
      }
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Something went wrong. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 relative overflow-hidden">
      {/* Theme toggle */}
      <div className="absolute top-4 right-4 z-10">
        <ThemeToggle />
      </div>
      {/* Background effects */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 -left-32 w-96 h-96 bg-primary/10 rounded-full blur-[120px] animate-pulse-glow" />
        <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-accent/10 rounded-full blur-[120px] animate-pulse-glow" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/5 rounded-full blur-[150px]" />
      </div>

      {/* Floating icons */}
      <div className="absolute top-20 left-20 text-primary/20 animate-float">
        <Ship className="w-16 h-16" />
      </div>
      <div className="absolute bottom-20 right-20 text-accent/20 animate-float" style={{ animationDelay: '2s' }}>
        <Anchor className="w-12 h-12" />
      </div>
      <div className="absolute top-1/3 right-1/4 text-primary/10 animate-float" style={{ animationDelay: '4s' }}>
        <Waves className="w-20 h-20" />
      </div>

      {/* Logo */}
      <div className="text-center mb-10 animate-fade-in relative z-10">
        <div className="flex items-center justify-center gap-3 mb-5">
          <div className="w-11 h-11 rounded-xl bg-primary flex items-center justify-center">
            <Ship className="w-5 h-5 text-primary-foreground" />
          </div>
          <div className="editorial-eyebrow">AI Mastery Academy</div>
        </div>
        <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight leading-[1.05] mb-3">
          Learn AI, the<br className="hidden sm:inline" /> <span className="text-brass italic">maritime</span> way.
        </h1>
        <p className="text-muted-foreground max-w-md mx-auto">Built for the dry cargo industry — by maritime experts.</p>
      </div>

      {/* Auth Card */}
      <Card className="w-full max-w-md glass-strong animate-slide-up">
        <CardHeader className="text-center">
          <h2 className="text-xl font-semibold leading-none tracking-tight">
            {isForgotPassword
              ? 'Reset Password'
              : isLogin
              ? 'Welcome Back, Captain'
              : 'Join the Fleet'}
          </h2>
          <CardDescription>
            {isForgotPassword
              ? 'Enter your email to receive a reset link'
              : isLogin
              ? 'Sign in to continue your learning voyage'
              : 'Create an account to start mastering AI'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isForgotPassword ? (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">Email</label>
                <Input
                  type="email"
                  placeholder="captain@shipping.com"
                  value={email}
                  onChange={handleEmailChange}
                  onBlur={handleEmailBlur}
                  required
                  className={`bg-background/50 ${emailError ? 'border-destructive' : ''}`}
                />
                {emailError && <p className="text-sm text-destructive">{emailError}</p>}
              </div>

              <Button
                type="submit"
                className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-bold py-6 glow"
                disabled={isResetLoading}
              >
                {isResetLoading ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                    Sending...
                  </span>
                ) : (
                  'Send Reset Link'
                )}
              </Button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => setIsForgotPassword(false)}
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  <span className="font-semibold text-primary">← Back to Sign In</span>
                </button>
              </div>
            </form>
          ) : (
            <>
              {!isLogin && (
                <div className="mb-4 rounded-xl bg-muted/50 border border-border/50 p-4 space-y-3">
                  <p className="text-sm font-semibold text-foreground">What you'll get:</p>
                  <p className="text-xs text-muted-foreground">Created by maritime industry experts</p>
                  <div className="grid grid-cols-1 gap-2.5">
                    {[
                      { icon: BookOpen, title: 'AI-Powered Lessons', desc: 'Learn AI concepts tailored for dry cargo shipping' },
                      { icon: Brain, title: 'Interactive Quizzes', desc: 'Test your knowledge at multiple difficulty levels' },
                      { icon: BarChart3, title: 'Progress Tracking', desc: 'Track completed lessons and quiz scores' },
                      { icon: Trophy, title: 'Leaderboard', desc: 'Compete with fellow maritime professionals' },
                    ].map(({ icon: Icon, title, desc }) => (
                      <div key={title} className="flex items-start gap-2.5">
                        <div className="mt-0.5 rounded-lg bg-primary/10 p-1.5">
                          <Icon className="w-3.5 h-3.5 text-primary" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground leading-tight">{title}</p>
                          <p className="text-xs text-muted-foreground leading-tight">{desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <form onSubmit={handleSubmit} className="space-y-4">
                {!isLogin && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">Full Name</label>
                    <Input
                      type="text"
                      placeholder="Captain John Doe"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="bg-background/50"
                    />
                  </div>
                )}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Email</label>
                  <Input
                    type="email"
                    placeholder="captain@shipping.com"
                    value={email}
                    onChange={handleEmailChange}
                    onBlur={handleEmailBlur}
                    required
                    className={`bg-background/50 ${emailError ? 'border-destructive' : ''}`}
                  />
                  {emailError && <p className="text-sm text-destructive">{emailError}</p>}
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Password</label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={6}
                      className="bg-background/50 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors p-1 min-w-[24px] min-h-[24px] flex items-center justify-center"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {!isLogin && <PasswordStrengthIndicator password={password} />}
                </div>

                {!isLogin && (
                  <>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-foreground">Confirm Password</label>
                      <div className="relative">
                        <Input
                          type={showPassword ? "text" : "password"}
                          placeholder="••••••••"
                          value={confirmPassword}
                          onChange={(e) => {
                            setConfirmPassword(e.target.value);
                            if (confirmPasswordError) {
                              setConfirmPasswordError(e.target.value === password ? '' : 'Passwords do not match');
                            }
                          }}
                          onBlur={() => {
                            if (confirmPassword && confirmPassword !== password) {
                              setConfirmPasswordError('Passwords do not match');
                            } else {
                              setConfirmPasswordError('');
                            }
                          }}
                          required
                          className={`bg-background/50 pr-10 ${confirmPasswordError ? 'border-destructive' : ''}`}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors p-1 min-w-[24px] min-h-[24px] flex items-center justify-center"
                          aria-label={showPassword ? "Hide password" : "Show password"}
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      {confirmPasswordError && <p className="text-sm text-destructive">{confirmPasswordError}</p>}
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-start space-x-2">
                        <Checkbox
                          id="terms"
                          checked={agreedToTerms}
                          onCheckedChange={(checked) => {
                            setAgreedToTerms(checked === true);
                            if (checked) setTermsError('');
                          }}
                          className={`mt-0.5 border-muted-foreground/50 ${termsError ? 'border-destructive' : ''}`}
                        />
                        <label
                          htmlFor="terms"
                          className="text-sm text-muted-foreground cursor-pointer leading-tight"
                        >
                          I agree to the{' '}
                          <a
                            href="/terms"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:text-primary/80 underline transition-colors"
                            onClick={(e) => e.stopPropagation()}
                          >
                            Terms of Service
                          </a>{' '}
                          and{' '}
                          <a
                            href="/privacy"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:text-primary/80 underline transition-colors"
                            onClick={(e) => e.stopPropagation()}
                          >
                            Privacy Policy
                          </a>
                        </label>
                      </div>
                      {termsError && <p className="text-sm text-destructive">{termsError}</p>}
                    </div>
                  </>
                )}

                {isLogin && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="remember-me"
                        checked={rememberMe}
                        onCheckedChange={(checked) => setRememberMe(checked === true)}
                        className="border-muted-foreground/50"
                      />
                      <label
                        htmlFor="remember-me"
                        className="text-sm font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
                      >
                        Remember me
                      </label>
                    </div>
                    <button
                      type="button"
                      onClick={() => setIsForgotPassword(true)}
                      className="text-sm text-primary hover:text-primary/80 transition-colors"
                    >
                      Forgot password?
                    </button>
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-bold py-6 glow"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                      Setting Sail...
                    </span>
                  ) : isLogin ? (
                    'Sign In'
                  ) : (
                    'Create Account'
                  )}
                </Button>
              </form>

              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground">Or continue with</span>
                </div>
              </div>

              {/* Social Sign In Buttons */}
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1 py-6 bg-background/50 hover:bg-background/80"
                  onClick={handleGoogleSignIn}
                  disabled={isGoogleLoading}
                  aria-label="Sign in with Google"
                >
                  {isGoogleLoading ? (
                    <div className="w-4 h-4 border-2 border-muted-foreground/30 border-t-muted-foreground rounded-full animate-spin" />
                  ) : (
                    <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
                      <path
                        fill="currentColor"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="currentColor"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="currentColor"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="currentColor"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                  )}
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  className="flex-1 py-6 bg-background/50 hover:bg-background/80"
                  onClick={handleAppleSignIn}
                  disabled={isAppleLoading}
                  aria-label="Sign in with Apple"
                >
                  {isAppleLoading ? (
                    <div className="w-4 h-4 border-2 border-muted-foreground/30 border-t-muted-foreground rounded-full animate-spin" />
                  ) : (
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                    </svg>
                  )}
                </Button>
              </div>

              <div className="mt-6 text-center">
                <button
                  onClick={() => setIsLogin(!isLogin)}
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  {isLogin ? "Don't have an account? " : 'Already have an account? '}
                  <span className="font-semibold text-primary">
                    {isLogin ? 'Sign up' : 'Sign in'}
                  </span>
                </button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Footer */}
      <p className="mt-8 text-xs text-muted-foreground/50 animate-fade-in">
        Powered by Gemini 3 Pro • Built for OT
      </p>
    </div>
  );
};

export default AuthScreen;
